import { isHttpUrl, normalizeFileImportUrl } from "./projectCsvImportFiles";

export const PERMITS_JSON_FIELD_ID = "engineering_details.permits";

const PERMIT_FILE_KEYS = new Set(["permit_plan", "permit_application", "permit"]);
const PERMIT_DATE_KEYS = new Set([
  "application_submitted_date",
  "permit_received_date",
  "permit_expiration_date",
]);

/** Legacy / export column names → schema keys. */
const PERMIT_ROW_KEY_ALIASES = {
  permit_plans: "permit_plan",
  permit_plan_url: "permit_plan",
  permit_application_url: "permit_application",
  permit_url: "permit",
  application_date: "application_submitted_date",
  submitted_date: "application_submitted_date",
  permit_issued_date: "permit_received_date",
  received_date: "permit_received_date",
  expiration_date: "permit_expiration_date",
  permit_no: "permit_number",
  permit_num: "permit_number",
};

export const PERMITS_JSON_HEADER_ALIASES = [
  "permits",
  "engineering_permits",
  "permits_json",
  "engineering_details_permits",
  "permit_files",
];

export function isPermitsJsonImportFieldId(fieldId) {
  return fieldId === PERMITS_JSON_FIELD_ID;
}

function normalizePermitRowKey(key) {
  const raw = String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_|_$/g, "");
  if (!raw) return "";
  return PERMIT_ROW_KEY_ALIASES[raw] || raw;
}

function normalizePermitScalar(key, value) {
  if (value == null) return undefined;
  const s = typeof value === "string" ? value.trim() : value;
  if (s === "") return undefined;
  if (key === "agency") return String(s);
  if (key === "location") return String(s);
  if (PERMIT_DATE_KEYS.has(key)) return String(s);
  if (PERMIT_FILE_KEYS.has(key)) {
    const url = normalizeFileImportUrl(s);
    return isHttpUrl(url) ? url : undefined;
  }
  if (key === "materials") return value;
  if (key === "riders") return value;
  return String(s);
}

function normalizePermitRiderRow(row) {
  if (row == null || typeof row !== "object" || Array.isArray(row)) return null;
  const out = {};
  for (const [rawKey, rawVal] of Object.entries(row)) {
    const key = normalizePermitRowKey(rawKey);
    if (!key) continue;
    const val = normalizePermitScalar(key, rawVal);
    if (val !== undefined) out[key] = val;
  }
  if (out.rider_type == null && row.type != null) out.rider_type = String(row.type);
  return Object.keys(out).length ? out : null;
}

function normalizePermitMaterials(materialsRaw) {
  if (materialsRaw == null) return undefined;
  if (Array.isArray(materialsRaw)) return materialsRaw;
  if (typeof materialsRaw === "object") {
    const keys = Object.keys(materialsRaw).sort((a, b) => Number(a) - Number(b));
    return keys.map((k) => materialsRaw[k]).filter(Boolean);
  }
  return undefined;
}

function normalizePermitRiders(ridersRaw) {
  if (ridersRaw == null) return undefined;
  const rows = Array.isArray(ridersRaw)
    ? ridersRaw
    : typeof ridersRaw === "object"
      ? Object.keys(ridersRaw)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => ridersRaw[k])
      : [];
  const normalized = rows.map(normalizePermitRiderRow).filter(Boolean);
  return normalized.length ? normalized : undefined;
}

function normalizePermitRow(row) {
  if (row == null || typeof row !== "object" || Array.isArray(row)) return null;
  const out = {};
  for (const [rawKey, rawVal] of Object.entries(row)) {
    const key = normalizePermitRowKey(rawKey);
    if (!key) continue;
    if (key === "materials") {
      const mats = normalizePermitMaterials(rawVal);
      if (mats?.length) out.materials = mats;
      continue;
    }
    if (key === "riders") {
      const riders = normalizePermitRiders(rawVal);
      if (riders?.length) out.riders = riders;
      continue;
    }
    const val = normalizePermitScalar(key, rawVal);
    if (val !== undefined) out[key] = val;
  }
  return Object.keys(out).length ? out : null;
}

function unwrapPermitsJsonCell(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";
  // Only unwrap a CSV-style quoted cell (outer quotes). Do not replace "" globally — that
  // breaks JSON empty strings like "permit_application": "".
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    const inner = s.slice(1, -1).replace(/""/g, '"').trim();
    if (inner.startsWith("{") || inner.startsWith("[")) return inner;
  }
  if (s.length >= 2 && s.startsWith("'") && s.endsWith("'")) {
    const inner = s.slice(1, -1).trim();
    if (inner.startsWith("{") || inner.startsWith("[")) return inner;
  }
  return s;
}

function tryParsePermitsJson(raw) {
  const s = unwrapPermitsJsonCell(raw);
  if (!s) return null;
  try {
    let parsed = JSON.parse(s);
    if (typeof parsed === "string") {
      const inner = unwrapPermitsJsonCell(parsed);
      if (inner) parsed = JSON.parse(inner);
    }
    return parsed;
  } catch {
    return null;
  }
}

function sortRepeaterKeys(keys) {
  return [...keys].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
}

function rowsFromParsedJson(parsed) {
  if (Array.isArray(parsed)) {
    return parsed.map(normalizePermitRow).filter(Boolean);
  }
  if (parsed && typeof parsed === "object") {
    return sortRepeaterKeys(Object.keys(parsed))
      .map((k) => normalizePermitRow(parsed[k]))
      .filter(Boolean);
  }
  return [];
}

/**
 * Parse a CSV cell containing permits JSON (numeric-key object or array of rows).
 * @returns {object[]|undefined}
 */
export function parsePermitsJsonFromCsvCell(raw) {
  if (raw == null) return undefined;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const rows = rowsFromParsedJson(raw);
    return rows.length ? rows : undefined;
  }
  const s = typeof raw === "string" ? raw.trim() : raw;
  if (s === "") return undefined;

  const parsed = typeof s === "string" ? tryParsePermitsJson(s) : s;
  if (parsed == null) return undefined;

  const rows = rowsFromParsedJson(parsed);
  return rows.length ? rows : undefined;
}

export function coercePermitsJsonImportValue(fieldId, raw) {
  if (!isPermitsJsonImportFieldId(fieldId)) return undefined;
  return parsePermitsJsonFromCsvCell(raw);
}

export function formatPermitsImportPreviewValue(rows) {
  if (!Array.isArray(rows) || !rows.length) return "—";
  const agencies = rows
    .map((r) => (r?.agency != null ? String(r.agency).trim() : ""))
    .filter(Boolean);
  if (agencies.length) return `${rows.length} permit(s): ${agencies.join(", ")}`;
  return `${rows.length} permit(s)`;
}
